import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_CUSTOM_DOMAIN_DELETION_ERRORS } from "@lib/shared_constants";

export type CustomDomainType = "link_shortening";
export function isCustomDomainType(type: string): type is CustomDomainType {
  return ["link_shortening"].includes(type);
}

export type CustomDomainStatus =
  | "new" // Record is created in our DB, but not yet created in the DNS provider
  | "active" // Record is active, ready to use by the customers
  | "pending_validation" // Pending validations, waiting TLS certificate generation
  | "action_required" // Action required, waiting for customer DNS setup
  | "expired" // Expired after the customer failed to finish the DNS setup in time
  | "error" // Some error occurred on the Cloudflare side
  | "unknown"; // We received an unknown status from Cloudflare
export function isCustomDomainStatus(status: string): status is CustomDomainStatus {
  return ["new", "active", "pending_validation", "action_required", "expired", "error", "unknown"].includes(status);
}

export type CustomDomainTlsStatus =
  | "active" // Record is active, ready to use by the customers
  | "pending_validation" // Pending validations, TLS cert generation
  | "deleted" // Deleted by the customer
  | "error" // In case of any error reported by Cloudflare (e.g. abuse)
  | "unknown"; // In case we receive an unknown status from Cloudflare
export function isCustomDomainTlsStatus(status: string): status is CustomDomainTlsStatus {
  return ["active", "pending_validation", "deleted", "error", "unknown"].includes(status);
}

export type DnsRecordStatus = "unknown" | "missing" | "incorrect" | "active";

interface BaseDnsRecord {
  type: string;
  name: string;
  value: string;
}

export interface DnsRecordWithoutFoundValue extends BaseDnsRecord {
  status: Exclude<DnsRecordStatus, "incorrect">;
  found_value?: never;
}

export interface DnsRecordWithFoundValue extends BaseDnsRecord {
  status: "incorrect";
  found_value: string;
}

export type DnsRecord = DnsRecordWithoutFoundValue | DnsRecordWithFoundValue;

export type CustomDomainMigrationType = "apex_proxying";
export function isCustomDomainMigrationType(type: string): type is CustomDomainMigrationType {
  return ["apex_proxying"].includes(type);
}

/** Body `action` for POST .../custom_domains/:domain_type/:domain_id/migration */
export type CustomDomainMigrationAction = "start" | "advance" | "finish";
export function isCustomDomainMigrationAction(action: string): action is CustomDomainMigrationAction {
  return ["start", "advance", "finish"].includes(action);
}

/** A completed entry stays in `migrations`, so never treat it as the current migration. */
export type CustomDomainMigrationState = "in_progress" | "completed";
export function isCustomDomainMigrationState(state: string): state is CustomDomainMigrationState {
  return ["in_progress", "completed"].includes(state);
}

export type CustomDomainMigrationStep =
  | "reassign_subscription_groups"
  | "lower_ttl"
  | "swap_dns_records" // Replaces the CNAME with the apex A/AAAA records
  | "wait_for_dns" // Waits for propagation, then verifies it
  | "restore_subscription_groups";
export function isCustomDomainMigrationStep(step: string): step is CustomDomainMigrationStep {
  return [
    "reassign_subscription_groups",
    "lower_ttl",
    "swap_dns_records",
    "wait_for_dns",
    "restore_subscription_groups",
  ].includes(step);
}

export interface CustomDomainMigrationSubscriptionGroup {
  id: string;
  /** Null when the snapshot references a subscription group that no longer exists. */
  name: string | null;
  app_group_id: string | null;
  /** Present on deletion usage responses. Migration snapshots omit it. */
  app_group_name?: string | null;
}

export interface CustomDomainAvailableMigration {
  type: CustomDomainMigrationType;
  migratable: boolean;
}

export interface CustomDomainMigration {
  type: CustomDomainMigrationType;
  state: CustomDomainMigrationState;
  step: CustomDomainMigrationStep;
  /** Snapshot taken at start. The set to restore in the final step. */
  subscription_groups?: CustomDomainMigrationSubscriptionGroup[];
  /**
   * Live, and not a subset of `subscription_groups` — a group can be pointed at the domain after the
   * snapshot, so never do set arithmetic between the two. Absent is not the same as empty.
   */
  remaining_subscription_groups?: CustomDomainMigrationSubscriptionGroup[];
  /** Seconds. Detail response only, and only on the TTL and swap steps. */
  dns_ttl?: number;
}

export interface CustomDomain {
  id: string;
  domain: string;
  status: CustomDomainStatus;
  tls_status?: CustomDomainTlsStatus;
  type: CustomDomainType;
  /** DNS provider. Link-shortening domains are `cloudflare` or `fastly`. */
  provider?: string | null;
  created_at?: number;
  expires_at?: number;
  expired_at?: number;
  activated_at?: number;
  /** The DNS records that need to be created in the DNS provider */
  dns_records?: DnsRecord[];
  available_migrations?: CustomDomainAvailableMigration[];
  migrations?: CustomDomainMigration[];
}

export interface DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdProps {
  companyId: string;
  domainType: CustomDomainType;
  domainId: string;
}

/** DELETE returns 204 with an empty body. */
export type DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdResult = Record<string, never>;

export type DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdInUseError = FetchUserEncodedError<
  SHARED_CONSTANTS_CUSTOM_DOMAIN_DELETION_ERRORS["IN_USE"],
  { subscription_groups: CustomDomainMigrationSubscriptionGroup[] }
>;

export type DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdUnsupportedProviderError = FetchUserEncodedError<
  SHARED_CONSTANTS_CUSTOM_DOMAIN_DELETION_ERRORS["UNSUPPORTED_PROVIDER"],
  { domain_id: string; provider: string }
>;

export type DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdNotFoundError = FetchUserEncodedError<
  SHARED_CONSTANTS_CUSTOM_DOMAIN_DELETION_ERRORS["NOT_FOUND"],
  never
>;

export type DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdResultErrors =
  | DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdInUseError
  | DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdUnsupportedProviderError
  | DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdNotFoundError;

export const isDeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdError = (
  error: FetchRequestError
): error is DeleteCompaniesCompanyIdCustomDomainsDomainTypeDomainIdResultErrors => {
  return error instanceof FetchUserEncodedError;
};
